/**
 * RichShotDetailContent Component
 *
 * Full-featured shot detail viewer with:
 * - Hexbin shot chart (display-only, no hex click interaction)
 * - Rich filter chips (% | TS%) with interactive label clicks for breakdowns
 * - Summary table with hierarchical breakdown rows
 * - Row hover/click drives chart highlighting
 *
 * Takes Shot[] directly — no API fetching.
 * Used by ShotDetailChartDialog (Explorer DataGrid, Player page).
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Stack, Typography, Button, Fade } from "@mui/material";
import {
  Shot,
  ShotFilters,
  DEFAULT_FILTERS,
  getVisibleCourtLength,
  COURT_CONSTANTS,
} from "./types";
import { applyFilters } from "./utils";
import ShotDetailChart from "./ShotDetailChart";
import ShotFilterChips from "./ShotFilterChips";
import RichShotDetailSummary from "./RichShotDetailSummary";
import "./ShotDetailModal.scss";

const CHART_WIDTH = 600;
const CHART_HEIGHT = Math.round(
  CHART_WIDTH * (getVisibleCourtLength() / COURT_CONSTANTS.COURT_WIDTH),
);

interface RichShotDetailContentProps {
  shots: Shot[];
  showCollegeLine?: boolean;
}

export default function RichShotDetailContent({
  shots,
  showCollegeLine,
}: RichShotDetailContentProps) {
  // Filter state
  const [filters, setFilters] = useState<ShotFilters>(DEFAULT_FILTERS);

  // Hierarchical breakdown state
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // Row highlight state for chart <-> summary interaction
  const [hoveredRow, setHoveredRow] = useState<{
    key: string;
    shots: Shot[];
  } | null>(null);
  const [selectedRow, setSelectedRow] = useState<{
    key: string;
    shots: Shot[];
  } | null>(null);

  // Effective shot subset for chart: row's shots override filtered shots
  const highlightedShots = useMemo(() => {
    const source = hoveredRow ?? selectedRow;
    return source?.shots ?? null;
  }, [hoveredRow, selectedRow]);

  // Filtered shots for normal rendering
  const filteredShots = useMemo(
    () => applyFilters(shots, filters),
    [shots, filters],
  );

  // Row interaction callbacks
  const handleRowHover = useCallback(
    (key: string | null, rowShots: Shot[] | null) => {
      setHoveredRow(key && rowShots ? { key, shots: rowShots } : null);
    },
    [],
  );

  const handleRowClick = useCallback((key: string, rowShots: Shot[]) => {
    setSelectedRow((prev) =>
      prev?.key === key ? null : { key, shots: rowShots },
    );
  }, []);

  // Clear selection when filters or breakdown groups change (rows are stale)
  useEffect(() => {
    setSelectedRow(null);
    setHoveredRow(null);
  }, [filters, expandedGroups]);

  // Expand group handler (click filter label -> breakdown)
  const handleExpandGroup = useCallback(
    (groupKey: string, isModifier: boolean) => {
      setExpandedGroups((prev) => {
        const index = prev.indexOf(groupKey);
        if (!isModifier) {
          // Regular click: replace with single level, or toggle off
          return prev.length === 1 && prev[0] === groupKey ? [] : [groupKey];
        }
        // Modifier (Cmd/Ctrl) click:
        if (index !== -1) {
          // Already in hierarchy — remove it and everything after
          return prev.slice(0, index);
        }
        if (prev.length >= 3) return prev; // Max depth
        return [...prev, groupKey]; // Append new level
      });
    },
    [],
  );

  // Whether any filter is active (differs from default) — drives reset button visibility
  const hasActiveFilters = useMemo(() => {
    const sf = filters;
    return (
      sf.assisted.size !== DEFAULT_FILTERS.assisted.size ||
      sf.fouled.size !== DEFAULT_FILTERS.fouled.size ||
      sf.shotType.size !== DEFAULT_FILTERS.shotType.size ||
      sf.contestLevel.size !== DEFAULT_FILTERS.contestLevel.size ||
      sf.catchAndShoot.size !== DEFAULT_FILTERS.catchAndShoot.size ||
      sf.regionCategory.size !== DEFAULT_FILTERS.regionCategory.size ||
      sf.shooter.size > 0 ||
      sf.passer.size > 0 ||
      (sf.contester?.size ?? 0) > 0
    );
  }, [filters]);

  const handleResetAll = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setExpandedGroups([]);
  }, []);

  return (
    <Box className="shot-detail-modal__content">
      <Stack spacing={2}>
        {/* Summary with interactive breakdown rows */}
        <RichShotDetailSummary
          shots={shots}
          filters={filters}
          expandedGroups={expandedGroups}
          onRowHover={handleRowHover}
          onRowClick={handleRowClick}
          selectedRowKey={selectedRow?.key ?? null}
        />

        {/* Chart + Filter chips side by side */}
        <Stack
          className="shot-detail-modal__chart-and-filters"
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems="flex-start"
        >
          {/* Chart */}
          <Box className="shot-detail-modal__chart-container">
            <ShotDetailChart
              shots={highlightedShots ?? filteredShots}
              filters={filters}
              width={CHART_WIDTH}
              showCollegeLine={showCollegeLine}
            />
          </Box>

          {/* Filter sidebar */}
          <Box
            className="shot-detail-modal__sidebar"
            sx={{ height: { xs: "auto", md: CHART_HEIGHT } }}
          >
            {/* Header + reset button */}
            <Box
              sx={{
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box />
              <Fade in={hasActiveFilters || expandedGroups.length > 0}>
                <Button
                  size="small"
                  onClick={handleResetAll}
                  disabled={!(hasActiveFilters || expandedGroups.length > 0)}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                    py: 0,
                    minWidth: "auto",
                    lineHeight: 1.2,
                  }}
                >
                  Reset filters
                </Button>
              </Fade>
            </Box>

            <Box className="shot-detail-modal__filters-scroll">
              <ShotFilterChips
                shots={shots}
                filters={filters}
                onChange={setFilters}
                expandedGroups={expandedGroups}
                onExpandGroup={handleExpandGroup}
              />
            </Box>
          </Box>
        </Stack>

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.disabled"
          className="shot-detail-modal__query-time"
        >
          {filteredShots.length.toLocaleString()} /{" "}
          {shots.length.toLocaleString()} shots
        </Typography>
      </Stack>
    </Box>
  );
}
